import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import Header from '../../components/Public/Header';
import Footer from '../../components/Public/Footer';
import useUpdateTitle from '../../hooks/updateTitle';
import { PRIVACY_POLICY } from '../../utils/StaticVariable/policyContent';
import { PREVIOUS_PRIVACY_POLICY } from '../../utils/StaticVariable/previousPrivacyPolicyContent';
import {
  PRIVACY_POLICY_REVISION_COMPARISON_ROWS,
  PRIVACY_POLICY_REVISION_NOTICE,
} from '../../utils/StaticVariable/privacyPolicyRevision';
import leftArrow from '../../icon/left.svg';

const tableHeadClass = 'border border-[#D9E0E7] bg-[#F3F7FA] px-3 py-3 text-left text-[12px] font-[700] text-[#1F2937] md:text-[13px]';
const tableCellClass = 'border border-[#D9E0E7] px-3 py-4 align-top text-[12px] leading-6 text-[#374151] md:text-[13px]';

const PrivacyPolicyRevisionPage = () => {
  useUpdateTitle();
  const isDesktop = useMemo(() => window.innerWidth > 1200, []);

  return (
    <div className="flex min-h-screen flex-col bg-[#F8FAFC]">
      {isDesktop ? null : (
        <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-[#E5E7EB] bg-white px-5 py-4">
          <Link
            to="/resume"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-[#F3F4F6]"
            aria-label="이전으로 이동"
          >
            <img
              src={leftArrow}
              alt=""
              className="h-5 w-5"
            />
          </Link>
          <div>
            <p className="text-[18px] font-[700] text-[#111827]">개인정보 처리방침 개정 안내</p>
            <p className="text-[12px] text-[#6B7280]">주요 변경사항 비교</p>
          </div>
        </div>
      )}

      {isDesktop ? <Header /> : null}

      <main className="flex-1 px-4 py-6 md:px-8 md:py-10">
        <div className="mx-auto w-full max-w-[1120px] rounded-[24px] border border-[#E5E7EB] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
          <div className="sticky top-0 z-10 flex flex-col gap-4 border-b border-[#E5E7EB] bg-white/95 px-5 py-4 backdrop-blur md:flex-row md:items-center md:justify-between md:px-8 md:py-6">
            <div>
              <p className="text-[20px] font-[700] text-[#111827] md:text-[28px]">{PRIVACY_POLICY_REVISION_NOTICE.title}</p>
              <p className="mt-1 text-[12px] text-[#6B7280] md:text-[14px]">
                공고일자: {PRIVACY_POLICY_REVISION_NOTICE.announcementDate} · 시행일자: {PRIVACY_POLICY_REVISION_NOTICE.effectiveDate}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/resume/privacy-policy"
                className="inline-flex items-center justify-center rounded-xl border border-[#D1D5DB] bg-white px-4 py-2 text-[13px] font-[600] text-[#374151] hover:bg-[#F9FAFB] md:px-5 md:py-2.5 md:text-[14px]"
              >
                전문보기
              </Link>
              <Link
                to="/resume"
                className="inline-flex items-center justify-center rounded-xl border border-[#D1D5DB] bg-white px-4 py-2 text-[13px] font-[600] text-[#374151] hover:bg-[#F9FAFB] md:px-5 md:py-2.5 md:text-[14px]"
              >
                메인으로
              </Link>
            </div>
          </div>

          <div className="space-y-8 px-4 py-5 md:px-8 md:py-8">
            <section className="rounded-2xl border border-[#D9F2EE] bg-[#F4FBFA] px-4 py-5 md:px-6 md:py-6">
              <p className="text-[18px] font-[700] text-[#111827] md:text-[24px]">개정사항 비교</p>
              <p className="mt-2 text-[13px] leading-7 text-[#374151] md:text-[15px]">
                {PRIVACY_POLICY_REVISION_NOTICE.description}
              </p>
            </section>

            <section name="detail-content">
              <h2 className="mb-4 text-[18px] font-[700] text-[#111827] md:text-[24px]">
                개인정보 처리방침 개정 전・후 비교
              </h2>
              <div className="overflow-x-auto rounded-xl border border-[#D9E0E7]">
                <table className="w-full min-w-[1180px] border-collapse">
                  <thead>
                    <tr>
                      <th className={`${tableHeadClass} w-[10%]`}>목차</th>
                      <th className={`${tableHeadClass} w-[45%]`}>개정 전</th>
                      <th className={`${tableHeadClass} w-[45%]`}>개정 후</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PRIVACY_POLICY_REVISION_COMPARISON_ROWS.map((change) => (
                      <tr key={change.section}>
                        <td className={`${tableCellClass} font-[700] text-[#111827]`}>{change.section}</td>
                        <td className={tableCellClass}>{change.previous}</td>
                        <td className={tableCellClass}>{change.current}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <details className="rounded-2xl border border-[#E5E7EB] bg-[#FAFAFA] px-4 py-5 md:px-6">
              <summary className="cursor-pointer text-[16px] font-[700] text-[#111827] md:text-[20px]">
                이전 개인정보 처리방침 전문보기
              </summary>
              <div className="mt-5">
                {PREVIOUS_PRIVACY_POLICY.map((content, index) => (
                  <div key={`previous-privacy-policy-${index}`}>{content}</div>
                ))}
              </div>
            </details>

            <details className="rounded-2xl border border-[#E5E7EB] bg-white px-4 py-5 md:px-6">
              <summary className="cursor-pointer text-[16px] font-[700] text-[#111827] md:text-[20px]">
                현행 개인정보 처리방침 전문보기
              </summary>
              <div className="mt-5">
              {PRIVACY_POLICY.map((content, index) => (
                <div key={`current-privacy-policy-${index}`}>{content}</div>
              ))}
              </div>
            </details>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyRevisionPage;
